import { Box, Chip, Link as MuiLink, Stack, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/utils/constants'
import type { UserStub } from '@/types/dashboard'
import type { WidgetProps } from './WidgetProps'
import { EmptyHint } from './EmptyHint'

interface DebtRow {
  contract_id: number
  student: UserStub | null
  balance_amount: string
}

interface PendingRow {
  id: number
  contract_id: number
  student: UserStub | null
  amount: string
  created_at: string | null
}

export function PendingPaymentsWidget({ payload }: WidgetProps) {
  const { t } = useTranslation()

  const debts = (payload.debts ?? []) as DebtRow[]
  const pending = (payload.pending_transactions ?? []) as PendingRow[]
  const debtsCount = Number(payload.debts_count ?? 0)
  const pendingCount = Number(payload.pending_count ?? 0)

  if (debtsCount === 0 && pendingCount === 0) {
    return <EmptyHint fallbackKey="dashboard.widgets.pending_payments.empty" />
  }

  const studentLink = (student: UserStub | null) =>
    student ? (
      <MuiLink
        component={Link}
        to={ROUTES.USER_FINANCE(student.id)}
        variant="body2"
        noWrap
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        {student.name}
      </MuiLink>
    ) : (
      <Typography variant="body2" sx={{ flexGrow: 1 }}>
        —
      </Typography>
    )

  return (
    <Stack spacing={1.5}>
      {debtsCount > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('dashboard.widgets.pending_payments.debts')} ({debtsCount})
          </Typography>
          <Stack spacing={0.5}>
            {debts.map((debt) => (
              <Box key={debt.contract_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {studentLink(debt.student)}
                <Chip size="small" color="warning" label={debt.balance_amount} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {pendingCount > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('dashboard.widgets.pending_payments.pending')} ({pendingCount})
          </Typography>
          <Stack spacing={0.5}>
            {pending.map((transaction) => (
              <Box key={transaction.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {studentLink(transaction.student)}
                <Chip size="small" color="default" label={transaction.amount} />
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  )
}
